/* eslint-disable max-lines */
import { animated, useSpring } from "@react-spring/web";
import { useAtom } from "jotai";
import { Loader, RotateCcw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { useDtfPricing } from "@design-lab/entities/artwork";
import { useDesignLabConfig, useShowArtistInstructions } from "@design-lab/shared/lib/config";
import { formatCurrency } from "@design-lab/shared/lib/currency";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Badge, Checkbox, Label, Switch } from "@design-lab/shared/ui";
import { SwitchInput } from "@design-lab/shared/ui/SwitchInput";
import { Textarea } from "@design-lab/shared/ui/textarea";

import { highlightJiffyDesignServiceAtom } from "@design-lab/features/design-artwork/model/print-perfect.model";
import { ArtistInstructions } from "@design-lab/features/design-artwork/ui/artist-instructions/ArtistInstructions";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { DesignServiceInfo } from "@design-lab/features/design-artwork/ui/DesignServiceInfo";
import { PrintPerfectExplanatoryTooltip } from "./PrintPerfectExplanatoryTooltip";

export type PrintPerfectProps = {
  canRemoveBackground?: boolean;
  showRefineArtwork?: boolean;
  canEditCutoutModal?: boolean;
  // Form-related props
  removeBackground: boolean;
  jiffyDesignServices: boolean;
  enhancementRequestNote?: string | undefined;
  editImageManual: boolean;
  instructionsError?: string | undefined;
  placementSide?: "front" | "back";
  // Handlers
  onRemoveBackgroundChange: (checked: boolean) => void;
  onJiffyDesignServicesChange: (checked: boolean) => void;
  onEnhancementRequestNoteChange: (value: string) => void;
  onRefineArtworkClick?: undefined | (() => void);
  onResetManualEdit?: undefined | (() => void);
  // Form registration for the textarea
  enhancementRequestNoteField: any;
  // Lens for SwitchInput (optional - only when used in design lab)
  removeBackgroundLens?: any;
};

export function UnionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.5171 21.8813C11.0315 22.0664 10.5204 22.2067 9.98877 22.2974C9.45721 22.3879 8.92901 22.4244 8.40967 22.4106L8.4751 19.9263C8.83367 19.9358 9.20046 19.9105 9.57178 19.8472C9.94297 19.7839 10.2973 19.6868 10.6323 19.5591L11.5171 21.8813ZM4.56787 18.1772C5.07367 18.7107 5.68117 19.1414 6.35205 19.4419L5.33643 21.7104C4.36903 21.2771 3.49352 20.6574 2.76416 19.8882L4.56787 18.1772ZM16.0083 17.6304C15.575 18.5979 14.9543 19.4732 14.1851 20.2026L12.4751 18.3989C13.0086 17.8931 13.4392 17.2847 13.7397 16.6138L16.0083 17.6304ZM3.03955 14.1782C3.03009 14.5365 3.05543 14.9029 3.11865 15.2739C3.18195 15.6452 3.27996 16.0003 3.40771 16.3354L1.08545 17.2202C0.900333 16.7345 0.759111 16.2237 0.668457 15.6919C0.577814 15.1601 0.54142 14.6314 0.555176 14.1118L3.03955 14.1782ZM19.271 1.44678C20.1465 0.63749 21.5064 0.664068 22.3501 1.50635C23.1938 2.34859 23.2202 3.70557 22.4097 4.57959L16.6753 10.7622C16.6727 10.765 16.6701 10.7682 16.6675 10.771L16.0776 11.4067C16.0578 11.4282 16.0375 11.4492 16.0181 11.4702C15.7509 11.7589 15.5207 12.0077 15.2563 12.1636C14.9387 12.3508 14.583 12.448 14.2261 12.4526C14.3012 13.3353 14.0003 14.244 13.3237 14.9194C12.6724 15.5694 11.7768 15.7655 10.9839 15.8286C10.446 15.8714 9.84837 15.8558 9.32764 15.8423C9.09763 15.8363 8.88248 15.8306 8.69385 15.8306C8.45244 15.8306 8.22794 15.7051 8.10205 15.4995C7.97648 15.294 7.96677 15.038 8.07666 14.8237C8.32381 14.3424 8.30092 13.7744 8.27002 13.0054C8.2691 12.9825 8.26899 12.9593 8.26807 12.936L8.26709 12.9185C8.23974 12.2342 8.19997 11.2304 8.91455 10.5171C9.59119 9.84162 10.501 9.54059 11.3853 9.61572C11.39 9.25957 11.4868 8.90434 11.6743 8.5874C11.8305 8.32354 12.0795 8.09333 12.3687 7.82666C12.3898 7.80718 12.4116 7.78796 12.4331 7.76807L19.271 1.44678ZM12.3442 11.4946C11.6677 10.8194 10.5706 10.8194 9.89404 11.4946C9.66765 11.7206 9.62096 12.0808 9.65283 12.8804C9.65492 12.9327 9.65715 12.9875 9.65967 13.0435C9.67802 13.4516 9.70029 13.9518 9.62061 14.4653C10.0451 14.4763 10.4572 14.4828 10.8735 14.4497C11.5673 14.3946 12.0486 14.2361 12.3442 13.9409C13.0208 13.2655 13.0208 12.17 12.3442 11.4946ZM4.78857 10.271C4.25521 10.7768 3.8244 11.3843 3.52393 12.0552L1.25537 11.0386C1.68868 10.0712 2.30945 9.19662 3.07861 8.46729L4.78857 10.271ZM13.3745 8.78271C12.9843 9.14338 12.9062 9.22649 12.8677 9.2915C12.7441 9.50034 12.7386 9.75874 12.854 9.97217C12.89 10.0386 12.9654 10.1243 13.3413 10.4995C13.7171 10.8746 13.8031 10.9499 13.8696 10.9858C14.0834 11.1011 14.3421 11.0955 14.5513 10.9722C14.6164 10.9338 14.6997 10.8559 15.061 10.4663L15.2007 10.3149L13.5259 8.64307L13.3745 8.78271ZM21.3706 2.48486C21.0535 2.16829 20.5425 2.15828 20.2134 2.4624L14.5435 7.70264L16.1431 9.29932L21.3921 3.64014C21.6967 3.31174 21.6874 2.80148 21.3706 2.48486ZM8.854 6.2583L8.78857 8.74268C8.43001 8.73318 8.06321 8.75848 7.69189 8.82178C7.32066 8.88507 6.96646 8.98312 6.63135 9.11084L5.74658 6.7876C6.23213 6.60257 6.74325 6.46221 7.2749 6.37158C7.80646 6.281 8.33467 6.24457 8.854 6.2583Z"
        fill="currentColor"
      />
    </svg>
  );
}

// eslint-disable-next-line max-lines-per-function
export function PrintPerfect({
  canRemoveBackground = true,
  showRefineArtwork = true,
  removeBackground,
  jiffyDesignServices,
  editImageManual,
  instructionsError,
  onRemoveBackgroundChange,
  onJiffyDesignServicesChange,
  onRefineArtworkClick,
  onResetManualEdit,
  enhancementRequestNoteField,
  removeBackgroundLens,
  enhancementRequestNote
}: PrintPerfectProps) {
  const [isConfirmToggleOpen, setIsConfirmToggleOpen] = useState(false);
  const [isConfirmResetOpen, setIsConfirmResetOpen] = useState(false);
  const jiffyDesignServicesId = useId();
  const designerInstructionsId = useId();
  const { data: pricingData } = useDtfPricing();
  const expertEditAddonTierPrice = pricingData?.expertEditAddonTierPrice;
  const showArtistInstructions = useShowArtistInstructions();
  const [shouldHighlight, setShouldHighlight] = useAtom(highlightJiffyDesignServiceAtom);
  const jiffyDesignServicesRef = useRef<HTMLDivElement>(null);
  const config = useDesignLabConfig();
  const isCustomerReviewOnRedrawsEnabled =
    config?.flip?.customerReviewOnRedraws && config?.flip.customerReviewOnPrintRedraws;

  // Shimmer animation - declarative approach (repeats 3 times)
  const shimmerStyles = useSpring({
    from: { x: -100, opacity: 0 },
    to: shouldHighlight
      ? [
          // First cycle
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Second cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Third cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } }
        ]
      : { x: -50, opacity: 0 },
    onRest: () => {
      if (shouldHighlight) {
        setShouldHighlight(false);
      }
    },
    config: { tension: 280, friction: 60 }
  });

  // Handle scrolling when highlight is triggered
  useEffect(() => {
    if (!shouldHighlight) return;

    jiffyDesignServicesRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }, [shouldHighlight]);

  const formattedPrice =
    expertEditAddonTierPrice > 0 ? formatCurrency(expertEditAddonTierPrice) : "Free";

  return (
    <div className="flex flex-col gap-4">
      {canRemoveBackground && (
        <div className="flex items-center space-x-2">
          {removeBackgroundLens ? (
            <SwitchInput
              data-testid="remove-background-switch"
              id="remove-background"
              lens={removeBackgroundLens}
              onCheckedChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          ) : (
            <Switch
              data-testid="remove-background-switch"
              id="remove-background"
              isSelected={removeBackground}
              onChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          )}
          <Label data-testid="remove-background-label" htmlFor="remove-background">
            Remove background
          </Label>
        </div>
      )}

      {showRefineArtwork && (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="refine-artwork-button"
            className="bg-cta-gradient hover:bg-cta-gradient-hover flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border-0 p-3 text-sm text-white"
            onClick={onRefineArtworkClick}
          >
            <UnionIcon className="h-4 w-4 text-white" />
            <span>Edit Design</span>
          </button>
          {editImageManual && (
            <button
              type="button"
              data-testid="reset-manual-edit-button"
              className="ml-3 flex cursor-pointer items-center gap-1 text-sm text-blue-700 hover:text-blue-800"
              onClick={() => setIsConfirmResetOpen(true)}
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset</span>
            </button>
          )}
        </div>
      )}

      <div
        ref={jiffyDesignServicesRef}
        className={cn(
          "relative flex items-center justify-between space-x-2 overflow-hidden rounded-lg bg-green-200 p-3 transition-all duration-300",
          shouldHighlight && "shadow-xl shadow-green-300/50 ring-2 ring-green-300"
        )}
      >
        {/* Primary shimmer element */}
        <animated.span
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
          style={{
            transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
            opacity: shimmerStyles.opacity
          }}
        />
        {/* Secondary glow layer for extra vibrancy */}
        <animated.span
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-green-100/50 to-transparent blur-xl"
          style={{
            transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
            opacity: shimmerStyles.opacity.to(o => o * 0.7)
          }}
        />
        <div className="flex flex-1 items-center space-x-2">
          <Checkbox
            data-testid="jiffy-design-services-switch"
            id={jiffyDesignServicesId}
            isSelected={jiffyDesignServices || false}
            onChange={checked => {
              const isChecked = checked === true;
              onJiffyDesignServicesChange(isChecked);
            }}
          />
          <div className="flex flex-col gap-2 lg:flex-row">
            <Label
              data-testid="jiffy-design-services-label"
              htmlFor={jiffyDesignServicesId}
              className="order-2 flex items-center gap-2 lg:order-1"
            >
              Jiffy Design Services
            </Label>
            {expertEditAddonTierPrice ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      "order-1 items-center gap-2 lg:order-2",
                      index === 0 ? "hidden lg:flex" : "flex lg:hidden"
                    )}
                  >
                    <Badge size="sm" className="bg-gray-700 font-medium">
                      Add-On
                    </Badge>
                    <p className="whitespace-nowrap text-sm text-green-700">{formattedPrice}</p>
                  </div>
                ))}
              </>
            ) : (
              <Loader className="animate-spin" />
            )}
          </div>
          <div className="flex flex-1 justify-end">
            <PrintPerfectExplanatoryTooltip>
              <DesignServiceInfo />
            </PrintPerfectExplanatoryTooltip>
          </div>
        </div>
      </div>

      {jiffyDesignServices && showArtistInstructions && <ArtistInstructions />}
      {jiffyDesignServices && !showArtistInstructions && (
        <div className="flex flex-col gap-2">
          <Label data-testid="designer-instructions-label" htmlFor={designerInstructionsId}>
            <em className="text-red-500">*</em>&nbsp; Instructions for our designers
          </Label>
          <Textarea
            data-testid="designer-instructions-textarea"
            {...enhancementRequestNoteField}
            id={designerInstructionsId}
            rows={4}
            placeholder='Example: "Change the color of the font to black. Remove the background from the top half of the image."'
            className={cn(instructionsError && "border-red-500")}
          />
          {instructionsError && <small className="text-red-500">{instructionsError}</small>}
          {enhancementRequestNote && isCustomerReviewOnRedrawsEnabled && (
            <span className="text-sm font-normal leading-5">
              <b>Note: </b>
              We will send a notification to review your redrawn artwork before printing. If there
              is no response within 1 hour, your order will proceed to print to prevent any further
              delays.
            </span>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={isConfirmToggleOpen}
        onClose={() => setIsConfirmToggleOpen(false)}
        title={`Turn ${removeBackground ? "Off" : "On"} Background Removal?`}
        message={`${removeBackground ? "Disabling" : "Enabling"} this will undo all background edits you've made. Do you want to continue?`}
        confirmText={`Turn ${removeBackground ? "Off" : "On"}`}
        cancelText="Keep Edits"
        onConfirm={() => {
          onRemoveBackgroundChange(!removeBackground);
          setIsConfirmToggleOpen(false);
        }}
      />
      <ConfirmActionModal
        isOpen={isConfirmResetOpen}
        onClose={() => setIsConfirmResetOpen(false)}
        title="Reset Edits?"
        message="This will discard all manual edits and restore the original image. Continue?"
        confirmText="Reset"
        cancelText="Keep Edits"
        onConfirm={() => {
          onResetManualEdit?.();
          setIsConfirmResetOpen(false);
        }}
      />
    </div>
  );
}
